/** Module 'notify': push, webhooks (Slack, Teams), digests (docs/product/CostTrackingMobileControl.md section 8). */
import type { Id, Ts } from '../index.ts';

export type Urgency = 'low' | 'normal' | 'high' | 'critical';
export type ChannelKind = 'push' | 'webhook' | 'email';

export interface Notification {
  id: Id;
  at: Ts;
  urgency: Urgency;
  title: string;
  body: string;
  /** Deep link into the app, e.g. /inbox?approval=apr_... */
  url: string;
  /** One-tap actions, where the channel supports them. */
  actions?: { id: string; title: string }[];
  ref?: { type: string; id: Id };
  /** When the request behind it stops accepting answers (approvals). Decisions after this are refused. */
  expiresAt?: Ts;
  /** What raised it: a trigger ('approval.pending'), 'escalation', 'digest', 'batch', 'test', or a module's own kind. */
  kind?: string;
  /** The project it is about, for per-project routing rules and rotas. */
  projectId?: Id | null;
  /** People (NotifyMember ids) it is for. Absent: everyone, by their own rules. */
  to?: Id[];
  /** Set on escalations: which step of which rota. */
  escalation?: { rotaId: Id; step: number; memberId: Id | null };
}

export interface Channel {
  id: Id;
  kind: ChannelKind;
  name: string;
  /** Lowest urgency this channel receives. */
  minUrgency: Urgency;
  /** Local time window when only critical gets through, e.g. { from: '22:00', to: '07:00' }. */
  quietHours?: { from: string; to: string };
  enabled: boolean;
}

/** state.ext.notify */
export interface NotifyState {
  /** Without secrets: webhook URLs are masked, push keys are never included. */
  channels: ChannelView[];
  /** Newest first, last 100. Every notification lives here even if push is off. */
  recent: Notification[];
  /** Public VAPID key for Web Push subscriptions. */
  vapidPublicKey: string | null;
  /** Escalations still open (an item waiting longer than its rota allows), oldest first. */
  escalations?: EscalationView[];
}

// ---------------------------------------------------------------------------
// Delivery details
// ---------------------------------------------------------------------------

export const URGENCIES: readonly Urgency[] = ['low', 'normal', 'high', 'critical'];

/** Rank for comparisons: low 0 .. critical 3. */
export function urgencyRank(u: Urgency): number {
  return URGENCIES.indexOf(u);
}

/** How a webhook channel formats its message. */
export type WebhookFormat = 'slack' | 'teams' | 'json';

/** A channel as the browser sees it: secrets (webhook URLs, push keys) are never sent. */
export interface ChannelView extends Channel {
  /** Webhook: its format. */
  format?: WebhookFormat;
  /** Webhook: host and the last characters of the URL, e.g. 'hooks.slack.com/...k3Xq'. */
  target?: string;
  /** Push: the browser or device it was registered from, e.g. 'Safari on iPhone'. */
  device?: string;
  /** Push: sha256 of the endpoint, so a browser can tell which channel is itself. */
  endpointHash?: string;
  createdAt: Ts;
  /** The last delivery attempt. */
  last?: { at: Ts; ok: boolean; message?: string };
  /** A person's own channel (their phone, their email). Absent: a shared channel (Slack #ops). */
  memberId?: Id | null;
  /** Only these kinds of notification (triggers, 'digest', 'escalation'...). Absent or null: all. */
  kinds?: string[] | null;
  /** Email: the address. */
  address?: string;
  /** Slack and Teams buttons and email links that act (approve, deny, acknowledge) without signing in. */
  oneTap?: boolean;
  /** Slack: a signing secret is set, so buttons answer in place through the Slack app. */
  interactive?: boolean;
}

/** What raises a notification by itself, from hub state. */
export type NotifyTrigger =
  | 'session.waiting'
  | 'session.ended'
  | 'session.unmetered'
  | 'approval.pending'
  | 'machine.offline'
  | 'pipeline.failed'
  | 'budget.alert'
  | 'mission.landed'
  | 'item.done'
  | 'deploy.done'
  | 'deploy.rolled-back';
export const NOTIFY_TRIGGERS: readonly NotifyTrigger[] = [
  'session.waiting',
  'session.ended',
  'session.unmetered',
  'approval.pending',
  'machine.offline',
  'pipeline.failed',
  'budget.alert',
  'mission.landed',
  'item.done',
  'deploy.done',
  'deploy.rolled-back',
];

export interface QuietHours {
  /** Local 'HH:MM'. A window may cross midnight ('22:00' to '07:00'). */
  from: string;
  to: string;
}

export interface NotifySettings {
  /** Default quiet hours for channels that do not set their own. */
  quietHours: QuietHours | null;
  /** Local time the daily digest goes out, 'HH:MM'; null turns the digest off. */
  digestAt: string | null;
  /** Which triggers are on. */
  triggers: Record<NotifyTrigger, boolean>;
  /** Where links in Slack and Teams messages point, e.g. a Tailscale https address. Null: no links. */
  publicUrl: string | null;
  /**
   * Low-urgency events roll into one message per channel at most this often (minutes).
   * Null: they wait for the daily summary.
   */
  batchMinutes?: number | null;
  /**
   * Whose wall clock quiet hours and the digest time read, as an IANA zone (Europe/Berlin).
   * Null or absent: the server's own clock (Orgus Cloud's is UTC).
   */
  timeZone?: string | null;
}

/** GET /api/notify: everything the Settings section shows. */
export interface NotifyView extends NotifyState {
  channels: ChannelView[];
  settings: NotifySettings;
  /** Low-urgency or quiet-hours notifications waiting for the next digest. */
  digestQueued: number;
  /** The email server, without its password. */
  smtp?: SmtpView;
  members?: NotifyMember[];
  rotas?: RotaView[];
}

/** The PushSubscription.toJSON() shape a browser hands over. */
export interface PushSubscriptionJson {
  endpoint: string;
  expirationTime?: number | null;
  keys: { p256dh: string; auth: string };
}

/** POST /api/notify/subscriptions */
export interface SubscribeRequest {
  subscription: PushSubscriptionJson;
  /** A label for the device, e.g. 'Safari on iPhone'. */
  device?: string;
  /** The phone app subscribed: pushed links open the same place in the phone layout (mobileUrl). */
  mobile?: boolean;
}

/** POST /api/notify/channels (webhooks) */
export interface WebhookChannelInput {
  name: string;
  format: WebhookFormat;
  url: string;
  minUrgency?: Urgency;
  quietHours?: QuietHours | null;
  memberId?: Id | null;
  kinds?: string[] | null;
  oneTap?: boolean;
  /** Slack only: the Slack app's signing secret, for buttons that answer in place. */
  signingSecret?: string | null;
}

/** POST /api/notify/channels with kind 'email'. */
export interface EmailChannelInput {
  kind: 'email';
  name: string;
  address: string;
  memberId?: Id | null;
  minUrgency?: Urgency;
  kinds?: string[] | null;
  oneTap?: boolean;
}

/** PATCH /api/notify/channels/:id */
export interface ChannelPatch {
  name?: string;
  minUrgency?: Urgency;
  quietHours?: QuietHours | null;
  enabled?: boolean;
  memberId?: Id | null;
  kinds?: string[] | null;
  oneTap?: boolean;
  /** Slack only: the Slack app's signing secret ('' or null clears it). Never sent back. */
  signingSecret?: string | null;
}

/** One channel's result of a send. */
export interface DeliveryResult {
  channelId: Id;
  outcome: 'sent' | 'digest' | 'skipped' | 'failed';
  message?: string;
}

/** POST /api/notify/test */
export interface TestSendResponse {
  notification: Notification;
  results: DeliveryResult[];
}

/** What a notification asks for; the module fills in id and time. */
export type NotificationInput = Omit<Notification, 'id' | 'at'> & { id?: Id; at?: Ts };

/** What other hub modules may ask the notify module: ctx.ext<NotifyApi>('notify'). */
export interface NotifyApi {
  /** Record the notification in the in-app list and route it to every channel. */
  send(n: NotificationInput): Promise<{ notification: Notification; results: DeliveryResult[] }>;
  /** The thing behind a notification was handled (answered, back online): stop escalating it. */
  resolve?(ref: { type: string; id: Id }): void;
}

/**
 * The JSON inside a Web Push message (what the service worker receives). Kept small:
 * push services cap the encrypted payload at about 4 KB.
 */
export interface PushPayload {
  id: Id;
  at: Ts;
  title: string;
  body: string;
  url: string;
  urgency: Urgency;
  actions?: { id: string; title: string }[];
  ref?: { type: string; id: Id };
  expiresAt?: Ts;
}

/**
 * The same place in the phone layout: push opens the installed app, so a notification for
 * /sessions?session=x lands on /m/session?session=x. Only app-relative paths are kept. A link
 * that names an approval (a blocked mission or session: ?approval=apr_x) opens the phone Inbox
 * on that request, where its blocked card and Approve and resume are (R-HL-1).
 */
export function mobileUrl(url: string): string {
  if (!url.startsWith('/') || url.startsWith('//')) return '/m';
  const qi = url.indexOf('?');
  const p = (qi >= 0 ? url.slice(0, qi) : url).split('#')[0]!;
  const search = qi >= 0 ? url.slice(qi).split('#')[0]! : '';
  const param = (name: string): string | null => {
    for (const pair of search.replace(/^\?/, '').split('&')) {
      const eq = pair.indexOf('=');
      const k = eq >= 0 ? pair.slice(0, eq) : pair;
      if (k === name) return eq >= 0 ? pair.slice(eq + 1) : '';
    }
    return null;
  };
  const under = (prefix: string) => p === prefix || p.startsWith(`${prefix}/`);
  if (under('/m')) return `${p}${search}`;
  if (p === '/') return '/m';
  if (under('/inbox')) return param('approval') ? `/m?approval=${param('approval')}` : '/m';
  if ((under('/missions') || under('/sessions')) && param('approval')) return `/m?approval=${param('approval')}`;
  if (under('/sessions')) return param('session') ? `/m/session?session=${param('session')}` : '/m/fleet';
  if (under('/machines')) return param('machine') ? `/m/fleet?machine=${param('machine')}` : '/m/fleet';
  if (under('/missions')) {
    const mission = p.startsWith('/missions/') ? p.slice('/missions/'.length).split('/')[0] : '';
    return mission ? `/m/missions?mission=${mission}` : '/m/missions';
  }
  if (under('/spend')) return '/m/costs';
  if (under('/settings')) return '/m/settings';
  return '/m/fleet';
}

/** SSE 'ext' events this module sends: type 'state' carries the whole NotifyState. */
export type NotifyEventType = 'state' | 'notification';

// ---------------------------------------------------------------------------
// Email (SMTP)
// ---------------------------------------------------------------------------

/** How the hub talks to the mail server: implicit TLS (465), STARTTLS (587) or plain (a local relay only). */
export type SmtpSecurity = 'tls' | 'starttls' | 'none';

/** The email server as the browser sees it: the password never leaves the hub. */
export interface SmtpView {
  configured: boolean;
  host: string;
  port: number;
  security: SmtpSecurity;
  user: string | null;
  hasPassword: boolean;
  /** The From address, e.g. 'Orgus <orgus@example.com>'. */
  from: string;
  last?: { at: Ts; ok: boolean; message?: string };
  /**
   * A mail provider sends instead of a server this Orgus keeps (Orgus Cloud: Cloudflare Email
   * Sending): no server form. `setup` says what whoever runs it does while it is not set up.
   */
  provider?: { name: string; setup?: string };
}

/** PUT /api/notify/smtp. password: undefined keeps the stored one, null or '' clears it. */
export interface SmtpInput {
  host: string;
  port: number;
  security: SmtpSecurity;
  user?: string | null;
  password?: string | null;
  from: string;
}

/** Which notifications an email channel gets by default (cost doc section 8): not the chatty ones. */
export const EMAIL_DEFAULT_KINDS: readonly string[] = ['budget.alert', 'machine.offline', 'session.unmetered', 'escalation', 'digest', 'batch', 'test'];

// ---------------------------------------------------------------------------
// People, routing rules, rotas and escalation
// ---------------------------------------------------------------------------

/** What a routing rule does with a notification it matches. Critical always gets through. */
export type RuleAction = 'always' | 'digest' | 'mute';

/** "Only critical after 22:00" is quiet hours; rules are the rest: per project, per kind. */
export interface RoutingRule {
  id: Id;
  /** Null: any project. */
  projectId: Id | null;
  /** A trigger or kind; null: any. */
  kind: string | null;
  action: RuleAction;
}

/** A person notifications are routed to. Their channels carry memberId. */
export interface NotifyMember {
  id: Id;
  name: string;
  /** The sign-in login (GitHub login, or 'owner') whose devices and in-app reads are theirs. */
  login: string | null;
  /** Slack member id (U0123ABC): who pressed a Slack button. */
  slackUserId: string | null;
  /** Their own quiet hours; null: the hub's. */
  quietHours: QuietHours | null;
  /** Lowest urgency on their own channels. */
  minUrgency: Urgency;
  /** First match wins. */
  rules: RoutingRule[];
  /** Gets the daily summary on their own channels. */
  dailySummary: boolean;
  createdAt: Ts;
}

/** POST /api/notify/members, PATCH /api/notify/members/:id */
export type NotifyMemberInput = Partial<Omit<NotifyMember, 'id' | 'createdAt'>>;

export type RotaRotation = 'manual' | 'daily' | 'weekly';

/** Who is on duty for a project, and who is next when they do not answer. */
export interface Rota {
  id: Id;
  name: string;
  /** Null: every project without a rota of its own. */
  projectId: Id | null;
  /** In duty order. */
  memberIds: Id[];
  /** Manual: the first person stays on duty until someone changes it. */
  rotation: RotaRotation;
  /** When the current order started; handoffs count from here. */
  anchor: Ts;
  /** Minutes a high-urgency item waits before it goes to the next person. Null: no escalation. */
  escalateAfterMin: number | null;
  /** High-urgency items page only the person on duty; everyone still sees them in the app. */
  onDutyOnly: boolean;
}

export interface RotaView extends Rota {
  onDuty: Id | null;
  /** When duty passes to the next person (daily and weekly). */
  nextHandoffAt: Ts | null;
}

/** POST /api/notify/rotas, PATCH /api/notify/rotas/:id */
export type RotaInput = Partial<Omit<Rota, 'id' | 'anchor'>> & { onDuty?: Id };

export type EscalationState = 'open' | 'resolved' | 'acknowledged' | 'exhausted';

/** A high-urgency item someone has not answered, walking down the rota. */
export interface EscalationView {
  /** ref type and id, e.g. 'approval:apr_01J...'. */
  key: string;
  ref: { type: string; id: Id };
  title: string;
  url: string;
  projectId: Id | null;
  rotaId: Id;
  /** 0: the person on duty; 1: the next; ... */
  step: number;
  /** Who has it now. */
  memberId: Id | null;
  startedAt: Ts;
  /** When it moves to the next person. */
  nextAt: Ts | null;
  state: EscalationState;
  closedAt?: Ts;
  closedBy?: string;
}

// ---------------------------------------------------------------------------
// The daily summary
// ---------------------------------------------------------------------------

export interface DailySummary {
  /** Local day it covers, 'YYYY-MM-DD'. */
  day: string;
  from: Ts;
  to: Ts;
  sessions: { active: number; started: number; ended: number; waitingNow: number; liveNow: number };
  landings: { passed: number; failed: number; items: { title: string; where: string; state: 'passed' | 'failed' }[] };
  /** Null when the costs module is not installed. */
  spend: { today: number; month: number; budgets: { id: Id; label: string; spent: number; limit: number; state: 'ok' | 'soft' | 'hard' }[] } | null;
  /** Null when the approvals module is not installed. */
  approvals: { asked: number; answered: number; expired: number; pendingNow: number; medianWaitSec: number | null; longestWaitSec: number | null } | null;
  /** Top issues for tomorrow, most urgent first. */
  issues: { title: string; detail: string; url: string; urgency: Urgency }[];
  /** ORGU-187: what Orgus did that day by itself. Absent when the missions module is not installed. */
  orgus?: OrgusDidToday;
}

/** The 'Orgus did today' section of the daily summary (ORGU-187). Spend is the summary's own. */
export interface OrgusDidToday {
  landed: { key: string | null; title: string; sha: string }[];
  done: { key: string; title: string }[];
  /** Items Autopilot held back for a person, with the one-line reason. */
  held: { key: string; title: string; reason: string }[];
  /** Problems Orgus fixed by itself (ADR 0064). */
  incidents: { title: string }[];
}

// ---------------------------------------------------------------------------
// One-tap actions outside Orgus, and the in-app centre
// ---------------------------------------------------------------------------

/** What a one-tap link does. 'approve' and 'deny' answer an approval; 'ack' stops an escalation. */
export type OneTapAction = 'approve' | 'deny' | 'ack';

/** GET /api/notify/act?t=: what a signed link would do, before anyone presses it. */
export interface OneTapPreview {
  action: OneTapAction;
  title: string;
  body: string;
  /** Who the link was made for: a person's name, or the channel. */
  forWhom: string;
  expiresAt: Ts;
  /** 'ready' or why it can no longer be used. */
  status: 'ready' | 'used' | 'expired' | 'answered';
  /** Where to look in Orgus. */
  url: string;
}

/** POST /api/notify/act */
export interface OneTapResult {
  ok: true;
  action: OneTapAction;
  message: string;
}

/** GET /api/notify/inbox: the signed-in person's place in the in-app list. */
export interface NotifyInbox {
  /** Notifications newer than this are unread. */
  lastSeenAt: Ts;
  /** The NotifyMember this sign-in maps to, if any. */
  memberId: Id | null;
}
